import { useRef, useEffect } from 'react';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { makeStyles } from '@material-ui/core/styles';
import { OkrTreeNode, GroupLead, ownerGroupSlug } from '../../hooks/useOkrEntities';
import { VerticalCard } from '../VerticalCard';

const CARD_WIDTH = 360;
const CARD_GAP = 16;

const useStyles = makeStyles(theme => ({
  section: {
    marginBottom: theme.spacing(3),
  },
  sectionLabel: {
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: '0.8px',
    color: theme.palette.text.secondary,
    textTransform: 'uppercase' as const,
    marginBottom: theme.spacing(0.5),
  },
  sectionDesc: {
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1.5),
  },
  carouselWrapper: {
    position: 'relative' as const,
  },
  arrow: {
    position: 'absolute' as const,
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 2,
    backgroundColor: theme.palette.background.paper,
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    '&:hover': {
      backgroundColor: theme.palette.background.paper,
    },
  },
  arrowLeft: {
    left: -20,
  },
  arrowRight: {
    right: -20,
  },
  viewport: {
    overflow: 'hidden' as const,
    margin: '0 24px',
  },
  track: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: CARD_GAP,
    padding: '8px 4px 20px',
    scrollBehavior: 'smooth' as const,
  },
  cardSlot: {
    minWidth: CARD_WIDTH,
    maxWidth: CARD_WIDTH,
  },
}));

interface VerticalCarouselProps {
  nodes: OkrTreeNode[];
  selectedIndex: number | null;
  onSelect: (index: number | null) => void;
  groupLeads?: Map<string, GroupLead>;
  sectionLabel?: string;
  sectionDesc?: string;
  chipLabel?: string;
  /** Allow clicking the active card to deselect (sets selectedIndex to null) */
  toggleable?: boolean;
  /** Called with the active objective index when the selected card changes its displayed objective */
  onObjChange?: (index: number) => void;
}

export function VerticalCarousel({ nodes, selectedIndex, onSelect, groupLeads, sectionLabel = 'Verticals', sectionDesc = 'Business units — the centred vertical scopes the horizontals below', chipLabel, toggleable = false, onObjChange }: VerticalCarouselProps) {
  const classes = useStyles();
  const trackRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!trackRef.current || selectedIndex === null) return;
    const containerWidth = trackRef.current.parentElement?.offsetWidth ?? 0;
    const targetScroll =
      selectedIndex * (CARD_WIDTH + CARD_GAP) - (containerWidth / 2 - CARD_WIDTH / 2);
    trackRef.current.scrollTo({ left: Math.max(0, targetScroll), behavior: 'smooth' });
  }, [selectedIndex]);

  if (!nodes.length) return null;

  return (
    <Box className={classes.section}>
      <Typography className={classes.sectionLabel}>{sectionLabel}</Typography>
      <Typography className={classes.sectionDesc}>{sectionDesc}</Typography>

      <Box className={classes.carouselWrapper}>
        <IconButton
          className={`${classes.arrow} ${classes.arrowLeft}`}
          size="small"
          disabled={selectedIndex === null || selectedIndex === 0}
          onClick={() => selectedIndex !== null && onSelect(selectedIndex - 1)}
        >
          <ChevronLeftIcon />
        </IconButton>

        <Box className={classes.viewport}>
          <div ref={trackRef} className={classes.track} style={{ overflowX: 'hidden' as const }}>
            {nodes.map((node, i) => (
              <Box key={node.entity.metadata.name} className={classes.cardSlot}>
                <VerticalCard
                  node={node}
                  selected={i === selectedIndex}
                  anySelected={selectedIndex !== null}
                  onClick={() => toggleable && i === selectedIndex ? onSelect(null) : onSelect(i)}
                  groupLead={groupLeads?.get(ownerGroupSlug(node.entity))}
                  chipLabel={chipLabel}
                  onObjChange={i === selectedIndex ? onObjChange : undefined}
                />
              </Box>
            ))}
          </div>
        </Box>

        <IconButton
          className={`${classes.arrow} ${classes.arrowRight}`}
          size="small"
          disabled={selectedIndex === null || selectedIndex === nodes.length - 1}
          onClick={() => selectedIndex !== null && onSelect(selectedIndex + 1)}
        >
          <ChevronRightIcon />
        </IconButton>
      </Box>
    </Box>
  );
}
